/**
 * Input primitive — design.md § Components: Inputs.
 *
 * With label + helper-text + error slots (replaces auth/form-controls TextInput).
 * Focus ring uses transition-base + accent border + accent-l ring.
 */
import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visible label (creates a <label> element). Optional when callers supply
   *  their own label/aria-label (e.g. filter inputs, inline forms). */
  label?: string;
  id?: string;
  helperText?: string;
  error?: string;
  icon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(
    {
      label,
      id,
      helperText,
      error,
      icon,
      className,
      ...props
    },
    ref,
  ) {
    const showWrapper = label !== undefined || helperText !== undefined || error !== undefined || icon !== undefined;
    const inputEl = (
      <input
        ref={ref}
        id={id}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={
          error && id ? `${id}-error` : helperText && id ? `${id}-helper` : undefined
        }
        className={cn(
          'w-full rounded-md border bg-card px-3.5 py-2.5 font-sans text-body text-ink',
          'placeholder:text-muted-2',
          'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l',
          'transition-base',
          error ? 'border-error' : 'border-line-2',
          icon ? 'pl-10' : '',
          className,
        )}
        {...props}
      />
    );

    // 15-FG/24: bare-input form (no wrapper, no label) for inline filters /
    // contexts where the caller already handles labelling via aria-label or
    // adjacent <label>. Use the wrapper variant when label/helperText/error/icon
    // are supplied.
    if (!showWrapper) return inputEl;

    return (
      <div className="flex flex-col gap-2">
        {label && id ? (
          <label htmlFor={id} className="font-sans text-small text-ink">
            {label}
          </label>
        ) : null}
        <div className="relative">
          {icon ? (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">
              {icon}
            </span>
          ) : null}
          {inputEl}
        </div>
        {helperText && !error && id ? (
          <p id={`${id}-helper`} className="font-sans text-small text-muted">
            {helperText}
          </p>
        ) : null}
        {error && id ? (
          <p id={`${id}-error`} className="font-sans text-small text-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
